<template>
  <div class="resource-analysis">
    <!-- 资源类型分布 -->
    <div class="type-chart chart-item">
      <div class="chart-header">
        <span class="chart-title">资源类型分布</span>
        <div class="total-info">
          <span class="total-value">{{ resourcesData.resourceStats.totalResources }}</span>
          <span class="total-label">总资源数</span>
        </div>
      </div>
      <div class="chart-container">
        <Echart :options="typeOptions" height="100%" />
      </div>
    </div>

    <!-- 资源使用趋势 -->
    <div class="trend-chart chart-item">
      <div class="chart-header">
        <span class="chart-title">资源使用趋势</span>
        <div class="total-info">
          <span class="total-value">{{ resourcesData.resourceStats.totalDownloads }}</span>
          <span class="total-label">总下载次数</span>
        </div>
      </div>
      <div class="chart-container">
        <Echart :options="trendOptions" height="100%" />
      </div>
    </div>

    <!-- 热门资源排行 -->
    <div class="ranking-info chart-item">
      <div class="chart-header">
        <span class="chart-title">热门资源排行</span>
      </div>
      <dv-scroll-board :config="rankingConfig" />
    </div>
  </div>
</template>

<script>
import Echart from '@/common/echart'
import resourcesData from '@/mock/teacher/resources.json'

export default {
  name: 'TeacherResourceAnalysis',
  components: {
    Echart
  },
  data() {
    return {
      resourcesData,
      chartHeight: '28vh',
      // 资源类型分布配置
      typeOptions: {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          textStyle: {
            color: '#fff'
          }
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['40%', '50%'],
            data: Object.entries(resourcesData.resourceStats.resourceTypes).map(([name, count], index) => ({
              name,
              value: count,
              itemStyle: {
                color: ['#37a2da', '#32c5e9', '#67e0e3', '#9fe6b8', '#ffdb5c'][index]
              }
            })),
            label: {
              show: true,
              formatter: '{b}: {d}%',
              color: '#fff'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '16',
                fontWeight: 'bold'
              }
            }
          }
        ]
      },

      // 资源使用趋势配置
      trendOptions: {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          top: '10%',
          right: '5%',
          bottom: '10%',
          left: '5%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
          axisLine: {
            lineStyle: {
              color: 'rgba(255,255,255,0.3)'
            }
          },
          axisLabel: {
            color: '#fff',
            interval: 0
          }
        },
        yAxis: {
          type: 'value',
          name: '下载次数',
          nameTextStyle: {
            color: '#fff'
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(255,255,255,0.3)'
            }
          },
          axisLabel: {
            color: '#fff'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(255,255,255,0.1)'
            }
          }
        },
        series: [
          {
            type: 'line',
            data: resourcesData.courses[0].resources.map(r => r.downloadCount),
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: {
              width: 3,
              color: '#37a2da'
            },
            itemStyle: {
              color: '#37a2da',
              borderWidth: 2,
              borderColor: '#fff'
            },
            areaStyle: {
              color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(55,162,218,0.5)' },
                { offset: 1, color: 'rgba(55,162,218,0.1)' }
              ])
            }
          }
        ]
      },

      // 热门资源排行配置
      rankingConfig: {
        header: ['排名', '资源名称', '下载次数', '资源类型'],
        data: resourcesData.courses[0].resources
          .sort((a, b) => b.downloadCount - a.downloadCount)
          .map((resource, index) => [
            (index + 1).toString(),
            resource.title,
            resource.downloadCount.toString(),
            resource.type
          ]),
        index: true,
        columnWidth: [50, 180, 80, 80],
        align: ['center'],
        rowNum: 5,
        headerBGC: 'rgba(0, 0, 0, 0.1)',
        headerHeight: 35,
        oddRowBGC: 'rgba(0, 0, 0, 0.15)',
        evenRowBGC: 'rgba(0, 0, 0, 0.1)',
        waitTime: 2000
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.resource-analysis {
  padding: 8px;
  height: 100%;
  display: grid;
  grid-template-rows: 30% 30% 40%;
  gap: 8px;

  .chart-item {
    background: rgba(6, 30, 93, 0.5);
    border-radius: 4px;
    padding: 8px;
    backdrop-filter: blur(4px);
    box-shadow: 0 0 10px rgba(0,0,0,0.2);
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .chart-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 4px;
      flex-shrink: 0;
      height: 28px;

      .chart-title {
        color: #fff;
        font-size: 14px;
        font-weight: 500;
        &::before {
          content: '';
          display: inline-block;
          width: 3px;
          height: 14px;
          background: #37a2da;
          margin-right: 6px;
          vertical-align: middle;
          border-radius: 2px;
        }
      }

      .total-info {
        display: flex;
        flex-direction: column;
        align-items: flex-end;

        .total-value {
          font-size: 18px;
          color: #37a2da;
          font-weight: bold;
        }

        .total-label {
          font-size: 12px;
          color: #fff;
          opacity: 0.8;
        }
      }
    }

    .chart-container {
      flex: 1;
      min-height: 0;
      height: calc(100% - 32px);
      position: relative;

      :deep(.echarts) {
        width: 100% !important;
        height: 100% !important;
      }
    }
  }
}
</style>